import { Autocomplete, Card, FormControlLabel, Switch } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useFormikContext } from "formik";
import { useMemo } from "react";

import FormField from "@/components/atoms/FormField";
import MDBox from "@/components/atoms/MDBox";
import MDInput from "@/components/atoms/MDInput";
import MDTypography from "@/components/atoms/MDTypography";
import MultiSelect from "@/components/atoms/MultiSelect";
import { CampaignFormData } from "@/components/screens/CampaignFormPage/types";
import { GENDER_OPTIONS } from "@/constants/gender.constants";
import useGetAllPlatforms from "@/hooks/requests/platform/use-get-all-platform";
import { CampaignFormMessages } from "@/messages/campaigns/campaign-form.messages";
import { PlatformCategory } from "@/types/platform.types";

import { getMonthOptions } from "../../form-data";

type Props = {
  messages: CampaignFormMessages;
};

const CampaignInfo = ({ messages }: Props) => {
  const { values, handleChange, errors, setFieldValue } = useFormikContext<CampaignFormData>();
  const { data: allPlatforms } = useGetAllPlatforms();

  const contactOriginOptions = useMemo(() => {
    if (!allPlatforms || !allPlatforms.items) return [];

    const platforms = allPlatforms.items.filter(
      (platform) =>
        platform.type === PlatformCategory.communication ||
        platform.type === PlatformCategory["e-commerce"]
    );

    return [
      { value: null, label: messages.noneOptionLabel },
      ...platforms.map((platform) => ({
        value: platform.name,
        label: platform.displayName,
      })),
    ];
  }, [allPlatforms, messages]);

  const monthOptions = getMonthOptions(messages);

  const selectedMonths = useMemo(() => {
    if (!values.segment.birthdayPeriod) return [];
    return values.segment.birthdayPeriod.split(",");
  }, [values.segment.birthdayPeriod]);

  return (
    <Card sx={{ height: "100%" }}>
      <MDBox p={3}>
        <MDBox display="flex" justifyContent="space-between">
          <MDTypography variant="h5" fontWeight="bold">
            {messages.contactInformationTitle}
          </MDTypography>
        </MDBox>

        <MDBox mt={1}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 12, md: 4 }}>
              <MultiSelect
                options={monthOptions}
                label={messages.birthdayPeriodLabel}
                value={selectedMonths}
                disabled={values.disabledField}
                onChange={(selectedValues) => {
                  const selectedValuesString = selectedValues.join(",");
                  setFieldValue("segment.birthdayPeriod", selectedValuesString || null);
                }}
                error={errors.segment?.birthdayPeriod}
                placeholder=""
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 12, lg: 4 }}>
              <Autocomplete
                fullWidth
                value={
                  GENDER_OPTIONS(messages).find(
                    (option) => option.value === values.segment.gender
                  ) || null
                }
                options={GENDER_OPTIONS(messages)}
                getOptionLabel={(option) => (option ? option.label : "")}
                isOptionEqualToValue={(option, value) => {
                  if (!option || !value) return false;
                  return option.value === value.value;
                }}
                onChange={(_, newValue) => {
                  if (newValue) setFieldValue("segment.gender", newValue.value);
                }}
                renderInput={(params) => (
                  <MDInput
                    label={messages.genderLabel}
                    {...params}
                    variant="standard"
                    error={!!errors.segment?.gender}
                    helperText={errors.segment?.gender}
                    disabled={values.disabledField}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 12, lg: 4 }}>
              <Autocomplete
                fullWidth
                value={
                  contactOriginOptions.find((opt) => opt.value === values.segment.contactSource) ||
                  null
                }
                options={contactOriginOptions}
                getOptionLabel={(option) => (option ? option.label : "")}
                isOptionEqualToValue={(option, value) => {
                  if (!option || !value) return false;
                  return option.value === value.value;
                }}
                onChange={(_, newValue) => {
                  setFieldValue("segment.contactSource", newValue ? newValue.value : null);
                }}
                renderInput={(params) => (
                  <MDInput
                    label={messages.contactOriginLabel}
                    {...params}
                    variant="standard"
                    error={!!errors.segment?.contactSource}
                    helperText={errors.segment?.contactSource}
                    disabled={values.disabledField}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 6, lg: 4 }}>
              <FormField
                variant="standard"
                name="segment.minimumAge"
                id="segment.minimumAge"
                label={messages.minAgeLabel}
                value={values.segment.minimumAge || ""}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.segment?.minimumAge}
                inputProps={{ min: 1 }}
                type="number"
                disabled={values.disabledField}
                autoComplete="off"
              />
            </Grid>

            <Grid size={{ xs: 6, lg: 4 }}>
              <FormField
                variant="standard"
                name="segment.maximumAge"
                id="segment.maximumAge"
                label={messages.maxAgeLabel}
                value={values.segment.maximumAge || ""}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.segment?.maximumAge}
                inputProps={{ min: 1 }}
                type="number"
                disabled={values.disabledField}
                autoComplete="off"
              />
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
    </Card>
  );
};

export default CampaignInfo;
